import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale'
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import GlassCard from '../../../shared/components/card/GlassCard';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../pages/Layout';
import { storageService } from '../service/storage';
import DotGrid from './DotGrid';
import styles from '../../../styles/Componentes/BienvenidoForm.module.css';

const BienvenidoForm = () => {
  const { getVentasPorDia, getVentasPorHora } = useAuth();
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [hourlyData, setHourlyData] = useState([]);
  const { currentDate, setIsLoading } = useDate();

  const fetchData = async () => {
    setLoading(true);
    setIsLoading(true);
    setError(null);
    const startTime = Date.now();

    try {
      const fecha = format(currentDate, 'yyyy-MM-dd');
      
      const [hourResponse, dayResponse] = await Promise.all([
        getVentasPorHora(fecha),
        getVentasPorDia(fecha)
      ]);

      setHourlyData(hourResponse.data || []);
      const ventaDelDia = dayResponse.data?.find(item => item.fecha === fecha);
      
      setName(storageService.getUserData()?.fullName || "");
      setSalesData(ventaDelDia || null);
      
    } catch (err) {
      console.error('Error cargando ventas:', err);
      setError('No se pudieron cargar los datos');
      setSalesData(null);
    } finally {
      const elapsed = Date.now() - startTime;
      const minDelay = 1000;
      
      setTimeout(() => {
        setLoading(false);
        setIsLoading(false);
      }, Math.max(0, minDelay - elapsed));
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const dailySales = salesData?.pvp ?? 0;
  const transactions = salesData?.operaciones ?? 0;
  const totalImport = salesData?.importe ?? 0;
  const totalUnits = salesData?.cantidad ?? 0;
  const avgTicket = salesData?.ticketMedio ?? 0;
  const avgTicketImporte = salesData?.ticketMedioImporte ?? 0;
  const avgTicketCant = salesData?.ticketMedioCantidad ?? 0;
  const fechaData = salesData?.fecha ?? format(currentDate, 'yyyy-MM-dd');
  
  const salesTrend = salesData?.porcentajePvp ?? 0;
  const transactionsTrend = salesData?.porcentajeOperaciones ?? 0;
  const importTrend = salesData?.porcentajeImporte ?? 0;
  const cantTrend = salesData?.porcentajeCantidad ?? 0;

  const isPositiveTrend = salesTrend >= 0;

  return (
    <div className={styles['dashboard-container']}>
      <GlassCard maxWidth='100%'>
        <header className={styles['dashboard-header']}>
          <div className={styles['welcome-section']}>
            <div className={styles['welcome-text']}>
              <h1>
                {(() => {
                  const hour = new Date().getHours();
                  let greeting;
                  if (hour >= 5 && hour < 12) {
                    greeting = '¡Buenos días';
                  } else if (hour >= 12 && hour < 20) {
                    greeting = '¡Buenas tardes';
                  } else {
                    greeting = '¡Buenas noches';
                  }
                  return name ? `${greeting}, ${name}!` : `${greeting}!`;
                })()}
              </h1>
              <p className={styles['welcome-date']}>
                {format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })
                .replace(/(?<=^|\s)\S/g, c => c.toUpperCase())
                .replace(/\bDe\b/g, 'de')}  
              </p>
            </div>
            
            <div className={styles['daily-stats-card']}>
              {loading ? (
                <div className={`${styles['stats-content']} ${styles['frosted-pulse']}`}>
                  <div className={styles['loading-overlay']}>
                    <DotGrid />
                  </div>
                  <div className={styles['blurred-content']}>
                    <div className={styles['main-stat']}>
                      <span className={styles['stat-label']}>Ventas Totales (PVP)</span>
                      <div className={styles['stat-value']}>
                        <span className={styles['amount']}>0,00</span>
                        <span className={styles['currency']}>€</span>
                      </div>
                    </div>
                    <DotGrid />
                  </div>
                </div>
                
              ) : error ? (
                <div className={`${styles['stats-content']} ${styles['frosted-error']}`}>
                  <div className={styles['error-overlay']}>
                    <div className={styles['error-icon']}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                    <span className={styles['error-text']}>{error}</span>
                    <button className={styles['retry-btn']} onClick={fetchData}>
                      Reintentar
                    </button>
                  </div>
                  <div className={styles['blurred-content']}>
                    <div className={styles['main-stat']}>
                      <span className={styles['stat-label']}>Ventas Totales (PVP)</span>
                      <div className={styles['stat-value']}>
                        <span className={styles['amount']}>----</span>
                        <span className={styles['currency']}>€</span>
                      </div>
                    </div>
                  </div>
                </div>
                
              ) : (
                <>
                  <div className={styles['stats-content']}>
                    <div className={styles['main-stat']}>
                      <span className={styles['stat-label']}>Ventas Totales (PVP)</span>
                      <div className={styles['stat-value']}>
                        <span className={styles['amount']}>
                          {dailySales.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </span>
                        <span className={styles['currency']}>€</span>
                      </div>
                      
                      <div className={`${styles['stat-trend']} ${isPositiveTrend ? styles['positive'] : styles['negative']}`}>
                        <span className={styles['trend-icon']}>{isPositiveTrend ? '↑' : '↓'}</span>
                        <span>{Math.abs(salesTrend).toFixed(1)}% vs período anterior</span>
                      </div>
                    </div>

                    <div className={styles['mini-chart']}>
                      <ResponsiveContainer width="100%" height={60}>
                        <AreaChart data={Array.isArray(hourlyData) ? hourlyData : []}>
                          <defs>
                            <linearGradient id="miniGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1397C5" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#1397C5" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area
                            type="monotone"
                            dataKey="pvpAcumulado"
                            stroke="#1397C5"
                            strokeWidth={2}
                            fill="url(#miniGradient)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className={styles['quick-stats']}>
                    <div className={styles['quick-stat-item']}>
                      <span className={styles['quick-label']}>Operaciones</span>
                      <span className={styles['quick-value']}>{transactions}</span>
                      {transactionsTrend !== 0 && (
                        <small className={`${styles['stat-trend']} ${transactionsTrend >= 0 ? styles['positive'] : styles['negative']}`}>
                          {transactionsTrend > 0 ? '+' : ''}{transactionsTrend.toFixed(1)}%
                        </small>
                      )}
                    </div>
                    
                    <div className={styles['quick-stat-divider']}></div>
                    
                    <div className={styles['quick-stat-item']}>
                      <span className={styles['quick-label']}>Unidades</span>
                      <span className={styles['quick-value']}>{totalUnits}</span>
                      {cantTrend !== 0 && (
                        <small className={`${styles['stat-trend']} ${cantTrend >= 0 ? styles['positive'] : styles['negative']}`}>
                          {cantTrend > 0 ? '+' : ''}{cantTrend.toFixed(1)}%
                        </small>
                      )}
                    </div>
                    
                    <div className={styles['quick-stat-divider']}></div>
                    
                    <div className={styles['quick-stat-item']}>
                      <span className={styles['quick-label']}>Importe Total</span>
                      <span className={styles['quick-value']}>
                        {totalImport.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}€
                      </span>
                      {importTrend !== 0 && (
                        <small className={`${styles['stat-trend']} ${importTrend >= 0 ? styles['positive'] : styles['negative']}`}>
                          {importTrend > 0 ? '+' : ''}{importTrend.toFixed(1)}%
                        </small>
                      )}
                    </div>
                    
                    <div className={styles['quick-stat-divider']}></div>
                    
                    <div className={`${styles['quick-stat-item']} ${styles['tickets-medios-item']}`}>
                      <span className={styles['quick-label']}>Tickets Medios</span>
                      <div className={styles['tickets-vertical']}>
                        <div className={styles['ticket-line']}>
                          <span className={styles['ticket-name']}>PVP</span>
                          <span className={styles['ticket-val']}>
                            {avgTicket.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                          </span>
                        </div>
                        <div className={styles['ticket-line']}>
                          <span className={styles['ticket-name']}>Importe</span>
                          <span className={styles['ticket-val']}>
                            {avgTicketImporte.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€
                          </span>
                        </div>
                        <div className={styles['ticket-line']}>
                          <span className={styles['ticket-name']}>Unidades</span>
                          <span className={styles['ticket-val']}>
                            {avgTicketCant.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
      </GlassCard>
    </div>
  );
};

export default BienvenidoForm;